import React, { useMemo } from 'react'
import {
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Typography,
} from '@material-ui/core'

import { useSignableDocs } from '../../../../../hooks/Outreach/useSignableDocs'
import type { StepEditPanelProps } from '../pipelineBuilder.types'
import type {
    ApplicationStepConfig,
    ApplicationVariantConfig,
    WorkbookGateConfig,
} from '../stepConfig.types'

const DEFAULT_GATE: WorkbookGateConfig = {
    screening_start: 'identity_complete',
    before_sign: 'workbook_complete',
    remainder_due: 'before_sign',
    pm_override: {
        allow_skip_to_screening: true,
        allow_advance_incomplete: true,
        require_reason: true,
    },
}

const QUALIFY_FIRST_VARIANTS: Array<{
    id: string
    qualifying_method: string
    doc_role: string
    label: string
}> = [
    {
        id: 'self',
        qualifying_method: 'self_qualified',
        doc_role: 'resident_application',
        label: 'Self-qualified',
    },
    {
        id: 'guarantor',
        qualifying_method: 'personal_guarantor',
        doc_role: 'guarantor_application',
        label: 'Personal guarantor',
    },
    {
        id: 'leap',
        qualifying_method: 'leap_gwp',
        doc_role: 'resident_application',
        label: 'Leap / GWP',
    },
    {
        id: 'prepay',
        qualifying_method: 'prepayment',
        doc_role: 'resident_application',
        label: 'Prepayment',
    },
]

const DOC_ROLE_LABELS: Record<string, string> = {
    resident_application: 'Resident application',
    guarantor_application: 'Guarantor application',
    lease_contract_guaranty: 'Lease contract guaranty',
    student_housing_lease: 'Student housing lease',
    apartment_lease: 'Apartment lease',
}

function defaultQualifyFirstApplications(): ApplicationVariantConfig[] {
    return QUALIFY_FIRST_VARIANTS.map((variant) => ({
        id: variant.id,
        qualifying_method: variant.qualifying_method,
        signable_doc_ref: { doc_role: variant.doc_role },
    }))
}

function mergeQualifyFirstApplications(
    existing?: ApplicationVariantConfig[],
): ApplicationVariantConfig[] {
    const byId = new Map(
        (existing ?? []).map((variant) => [variant.id, variant]),
    )
    return QUALIFY_FIRST_VARIANTS.map((template) => {
        const current = byId.get(template.id)
        if (current) {
            return current
        }
        return {
            id: template.id,
            qualifying_method: template.qualifying_method,
            signable_doc_ref: { doc_role: template.doc_role },
        }
    })
}

export const ApplicationStepPanel: React.FC<StepEditPanelProps> = ({
    step,
    apartmentId,
    readOnly,
    onChange,
}) => {
    const config = (step.config ?? {}) as ApplicationStepConfig
    const gate = config.workbook_gate ?? DEFAULT_GATE
    const mode = config.mode ?? 'collect_first'
    const applications = useMemo(
        () =>
            mode === 'qualify_first'
                ? mergeQualifyFirstApplications(config.applications)
                : config.applications ?? [],
        [config.applications, mode],
    )

    const { docs: readyDocs, isLoading: docsLoading } = useSignableDocs(
        apartmentId
            ? {
                  document_type: 'APPLICATION',
                  status: 'READY',
              }
            : undefined,
    )

    const patchConfig = (patch: Partial<ApplicationStepConfig>) => {
        onChange({
            config: {
                ...config,
                ...patch,
            },
        })
    }

    const patchGate = (patch: Partial<WorkbookGateConfig>) => {
        patchConfig({
            workbook_gate: {
                ...gate,
                ...patch,
                pm_override: {
                    ...gate.pm_override,
                    ...(patch.pm_override ?? {}),
                },
            },
        })
    }

    const patchVariant = (
        variantId: string,
        patch: Partial<ApplicationVariantConfig>,
    ) => {
        const next = applications.map((variant) =>
            variant.id === variantId ? { ...variant, ...patch } : variant,
        )
        patchConfig({ applications: next })
    }

    const docsForRole = (docRole: string) =>
        readyDocs.filter(
            (doc) =>
                doc.doc_role === docRole ||
                (!doc.doc_role && docRole === 'resident_application'),
        )

    return (
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Application mode</InputLabel>
                <Select
                    label="Application mode"
                    value={mode}
                    disabled={readOnly}
                    onChange={(e) => {
                        const nextMode = e.target
                            .value as ApplicationStepConfig['mode']
                        patchConfig({
                            mode: nextMode,
                            applications:
                                nextMode === 'qualify_first'
                                    ? mergeQualifyFirstApplications(
                                          config.applications,
                                      )
                                    : config.applications,
                        })
                    }}
                >
                    <MenuItem value="collect_first">Collect first</MenuItem>
                    <MenuItem value="qualify_first">Qualify first</MenuItem>
                </Select>
            </FormControl>

            {mode === 'qualify_first' && (
                <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
                    <Typography variant="subtitle2">
                        Application variants
                    </Typography>
                    {docsLoading && (
                        <Typography variant="body2" color="textSecondary">
                            Loading property signable docs…
                        </Typography>
                    )}
                    {QUALIFY_FIRST_VARIANTS.map((template) => {
                        const variant = applications.find(
                            (item) => item.id === template.id,
                        )
                        if (!variant) return null

                        const roleDocs = docsForRole(template.doc_role)
                        const selectedDocId = variant.signable_doc_id
                        const hasUnresolvedRef =
                            !selectedDocId && Boolean(variant.signable_doc_ref)
                        const selectValue =
                            selectedDocId != null
                                ? String(selectedDocId)
                                : hasUnresolvedRef
                                ? ''
                                : ''

                        return (
                            <Box key={template.id}>
                                <Typography variant="body2" gutterBottom>
                                    {template.label} —{' '}
                                    {DOC_ROLE_LABELS[template.doc_role] ??
                                        template.doc_role}
                                </Typography>
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    disabled={readOnly}
                                >
                                    <InputLabel>Signable document</InputLabel>
                                    <Select
                                        label="Signable document"
                                        value={selectValue}
                                        onChange={(e) => {
                                            const value = e.target
                                                .value as string
                                            if (!value) {
                                                patchVariant(template.id, {
                                                    signable_doc_id: undefined,
                                                    signable_doc_ref: {
                                                        doc_role:
                                                            template.doc_role,
                                                    },
                                                })
                                                return
                                            }
                                            patchVariant(template.id, {
                                                signable_doc_id: Number(value),
                                                signable_doc_ref: undefined,
                                            })
                                        }}
                                    >
                                        {hasUnresolvedRef && (
                                            <MenuItem value="">
                                                Resolve on apply (
                                                {
                                                    DOC_ROLE_LABELS[
                                                        template.doc_role
                                                    ]
                                                }
                                                )
                                            </MenuItem>
                                        )}
                                        {roleDocs.map((doc) => (
                                            <MenuItem
                                                key={doc.id}
                                                value={String(doc.id)}
                                            >
                                                {doc.name}
                                                {doc.catalog_template_key
                                                    ? ` — ${doc.catalog_template_key}`
                                                    : ''}
                                            </MenuItem>
                                        ))}
                                        {roleDocs.length === 0 &&
                                            !hasUnresolvedRef && (
                                                <MenuItem value="" disabled>
                                                    No READY docs for this role
                                                </MenuItem>
                                            )}
                                        {roleDocs.length > 0 &&
                                            readyDocs
                                                .filter(
                                                    (doc) =>
                                                        !roleDocs.some(
                                                            (match) =>
                                                                match.id ===
                                                                doc.id,
                                                        ),
                                                )
                                                .map((doc) => (
                                                    <MenuItem
                                                        key={`other-${doc.id}`}
                                                        value={String(doc.id)}
                                                    >
                                                        Other: {doc.name}
                                                    </MenuItem>
                                                ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )
                    })}
                </Box>
            )}

            <Typography variant="subtitle2">Workbook gates</Typography>

            <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Start screening when</InputLabel>
                <Select
                    label="Start screening when"
                    value={gate.screening_start}
                    disabled={readOnly}
                    onChange={(e) =>
                        patchGate({
                            screening_start: e.target
                                .value as WorkbookGateConfig['screening_start'],
                        })
                    }
                >
                    <MenuItem value="identity_complete">
                        Identity complete
                    </MenuItem>
                    <MenuItem value="workbook_complete">
                        Workbook complete
                    </MenuItem>
                    <MenuItem value="manual_only">Manual only</MenuItem>
                </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Before sign requires</InputLabel>
                <Select
                    label="Before sign requires"
                    value={gate.before_sign}
                    disabled={readOnly}
                    onChange={(e) =>
                        patchGate({
                            before_sign: e.target
                                .value as WorkbookGateConfig['before_sign'],
                        })
                    }
                >
                    <MenuItem value="workbook_complete">
                        Workbook complete
                    </MenuItem>
                    <MenuItem value="identity_complete">
                        Identity complete
                    </MenuItem>
                </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Remainder due</InputLabel>
                <Select
                    label="Remainder due"
                    value={gate.remainder_due}
                    disabled={readOnly}
                    onChange={(e) =>
                        patchGate({
                            remainder_due: e.target
                                .value as WorkbookGateConfig['remainder_due'],
                        })
                    }
                >
                    <MenuItem value="before_sign">Before sign</MenuItem>
                    <MenuItem value="before_lease">Before lease</MenuItem>
                    <MenuItem value="after_lease">After lease</MenuItem>
                    <MenuItem value="never">Never</MenuItem>
                </Select>
            </FormControl>

            <FormControlLabel
                control={
                    <Switch
                        checked={gate.pm_override.allow_skip_to_screening}
                        disabled={readOnly}
                        onChange={(e) =>
                            patchGate({
                                pm_override: {
                                    ...gate.pm_override,
                                    allow_skip_to_screening: e.target.checked,
                                },
                            })
                        }
                        color="primary"
                    />
                }
                label="PM may start screening before identity complete"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={gate.pm_override.allow_advance_incomplete}
                        disabled={readOnly}
                        onChange={(e) =>
                            patchGate({
                                pm_override: {
                                    ...gate.pm_override,
                                    allow_advance_incomplete: e.target.checked,
                                },
                            })
                        }
                        color="primary"
                    />
                }
                label="PM may advance with incomplete workbook"
            />

            <Typography variant="body2" color="textSecondary">
                Application fee amount is set on the Application Fee tab.
            </Typography>

            <FormControlLabel
                control={
                    <Switch
                        checked={config.require_review_on_complete ?? false}
                        disabled={readOnly}
                        onChange={(e) =>
                            patchConfig({
                                require_review_on_complete: e.target.checked,
                            })
                        }
                        color="primary"
                    />
                }
                label="Require PM review when application completes"
            />
        </Box>
    )
}

// exported for tests
export { defaultQualifyFirstApplications, DOC_ROLE_LABELS }
